/** @jsxImportSource @emotion/react */
import { faArrowTrendDown, faBell, faBoxTaped, faGrid, faList, faTemperatureLow } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Badge } from "reactstrap";
import { Tabs } from "../../../Helper/Tabs";
import { LIST_OR_GRID } from "../hooks/useRipeningRoom";

interface RipeningRoomViewTabsProps {
    setListOrGrid: (value: string) => void;
    filterLevel: string;
    setFilterLevel: (level: string) => void;
    cellLevels: string[];
    showGridOptions?: boolean;
    activeView?: "list" | "graph-pallets" | "grid" | "graph-check-session" | "graph-sensor-data" | "graph-fruit-sensor-data" | "signals";
    onViewChange?: (view: "list" | "graph-pallets" | "grid" | "graph-check-session" | "graph-sensor-data" | "graph-fruit-sensor-data" | "signals") => void;
    enableFruitSensorGraph?: boolean;
    signalsEnabled?: boolean;
    openSignalCount?: number;
}

const RipeningRoomViewTabs: React.FC<RipeningRoomViewTabsProps> = ({
    setListOrGrid,
    filterLevel,
    setFilterLevel,
    cellLevels,
    showGridOptions = true,
    activeView = "list",
    onViewChange,
    enableFruitSensorGraph = false,
    signalsEnabled = false,
    openSignalCount = 0
}) => {
    // Define tab type
    type TabType = {
        value: string;
        title: string | React.ReactNode;
        type: "main_view" | "level";
        onClick: () => void;
    };

    // Build tabs based on available options
    const buildTabs = (): TabType[] => {
        const tabs: TabType[] = [];

        // Always include List tab first
        tabs.push({
            value: "list",
            title: <span><span className="me-2"><FontAwesomeIcon icon={faList} /></span>List</span>,
            type: "main_view",
            onClick: () => {
                setListOrGrid(LIST_OR_GRID.LIST);
                onViewChange?.("list");
            }
        });

        // Room-scoped Signals worklist (R15), with an open-count badge.
        if (signalsEnabled) {
            tabs.push({
                value: "signals",
                title: (
                    <span>
                        <span className="me-2"><FontAwesomeIcon icon={faBell} /></span>
                        Signals
                        {openSignalCount > 0 && <Badge color="danger" pill className="ms-2">{openSignalCount}</Badge>}
                    </span>
                ),
                type: "main_view",
                onClick: () => onViewChange?.("signals"),
            });
        }

        // If levels are available, show level tabs for grid view
        if (showGridOptions && cellLevels.length > 0) {
            cellLevels.forEach((level) => {
                tabs.push({
                    value: level,
                    title: <span><span className="me-2"><FontAwesomeIcon icon={faGrid} /></span>{level}</span>,
                    type: "level" as const,
                    onClick: () => {
                        // Switch to grid view and set the level
                        setListOrGrid(LIST_OR_GRID.GRID);
                        setFilterLevel(level);
                        onViewChange?.("grid");
                    }
                });
            });
        }

        // Always include Graphs tab last
        tabs.push({
            value: "graph-pallets",
            title: <span><span className="me-2"><FontAwesomeIcon icon={faArrowTrendDown} /></span>Pallets</span>,
            type: "main_view",
            onClick: () => {
                onViewChange?.("graph-pallets");
            },
        });
        tabs.push({
            value: "graph-check-session",
            title: <span><span className="me-2"><FontAwesomeIcon icon={faArrowTrendDown} /></span>Check Sessions</span>,
            type: "main_view",
            onClick: () => {
                onViewChange?.("graph-check-session");
            }
        });
        tabs.push({
            value: "graph-sensor-data",
            title: <span><span className="me-2"><FontAwesomeIcon icon={faTemperatureLow} /></span>Room Sensor</span>,
            type: "main_view",
            onClick: () => {
                onViewChange?.("graph-sensor-data");
            }
        });
        if (enableFruitSensorGraph) {
            tabs.push({
                value: "graph-fruit-sensor-data",
                title: <span><span className="me-2"><FontAwesomeIcon icon={faBoxTaped} /></span>Fruit Sensor</span>,
                type: "main_view",
                onClick: () => {
                    onViewChange?.("graph-fruit-sensor-data");
                }
            });
        }

        return tabs;
    };


    // Determine if a tab is active
    const isTabActive = (tab: TabType) => {
        if (tab.type === "main_view") {
            return tab.value === activeView;
        }
        // Level tab is active if we're in grid view and this level is selected
        return tab.value === filterLevel && activeView === "grid";
    };

    const allTabs = buildTabs()?.map((tab) => ({ ...tab, isActive: isTabActive(tab) })) ?? [];

    return (
        <div>
            <Tabs tabs={allTabs} />
        </div>

    );
};

export default RipeningRoomViewTabs;
